import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { DOC_ROUTE_MAP, type DocAudience, type DocRouteEntry } from '@/generated/docRouteMap'
import { getAppMode } from '@/utils/domain'

/**
 * Resolves the documentation page for the current route and opens it on the
 * docs site (issue #2655). We link out to docs.scrivio.nl in a new tab rather
 * than rendering docs in-app.
 *
 * The route -> doc mapping is generated from the view pages' `route:` frontmatter
 * (`@/generated/docRouteMap`), the same source the docs coverage check (#2654)
 * joins against, so the two never drift apart.
 */

export const DOCS_BASE_URL = 'https://docs.scrivio.nl'

/**
 * Map the running app to the doc audience it should see. The admin portal reuses
 * the behandelaar app's configuration views, so it falls back to behandelaar docs.
 */
function currentDocAudience(): DocAudience {
  return getAppMode() === 'client' ? 'client' : 'behandelaar'
}

/**
 * Pick the doc entry for the current app from the list a route maps to. A route
 * documented for both audiences (e.g. `/appointments/:id`) carries one entry per
 * audience; we prefer the audience match. When a route has a single entry it is
 * audience-agnostic in practice (only reachable in one app), so we use it as-is.
 */
function selectEntry(entries: DocRouteEntry[], audience: DocAudience): DocRouteEntry | undefined {
  return entries.find((entry) => entry.audience === audience) ?? (entries.length === 1 ? entries[0] : undefined)
}

/** Build the absolute docs URL for a page, prefixing `/en` for the English locale. */
export function buildDocUrl(entry: DocRouteEntry, locale: string, anchor?: string): string {
  const localePrefix = locale === 'en' ? '/en' : ''
  const base = `${DOCS_BASE_URL}${localePrefix}/${entry.path}`
  return anchor ? `${base}#${anchor}` : base
}

export function useHelp() {
  const route = useRoute()
  const { locale } = useI18n()

  // Walk matched records from most specific to least so a child route's doc page
  // wins over a parent layout route that happens to be documented too.
  const entry = computed<DocRouteEntry | undefined>(() => {
    const audience = currentDocAudience()
    for (let i = route.matched.length - 1; i >= 0; i--) {
      const matched = route.matched[i]
      if (!matched) continue
      const found = DOC_ROUTE_MAP[matched.path]
      if (!found) continue
      const selected = selectEntry(found, audience)
      if (selected) return selected
    }
    return undefined
  })

  const hasHelp = computed(() => entry.value !== undefined)

  const helpUrl = computed(() =>
    entry.value ? buildDocUrl(entry.value, locale.value) : undefined,
  )

  /** Open the doc page (optionally deep-linked to an action anchor) in a new tab. */
  function openHelp(anchor?: string): void {
    if (!entry.value) return
    window.open(buildDocUrl(entry.value, locale.value, anchor), '_blank', 'noopener,noreferrer')
  }

  return { hasHelp, helpUrl, openHelp }
}
